"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import Image from "next/image";
import React, { useMemo, useRef } from "react";

export interface Banner8MetaItem {
  id?: string | number;
  priority?: number | string;
  status?: boolean;
  metaImg?: string;
  altImg?: string;
  metaTitle?: string;
  metaDescription?: string;
}

export interface Banner8Data {
  id?: string;
  status?: boolean;
  title?: string;
  description?: string;
  image?: string;
  mobile_image?: string;
  image_alt?: string;
  background_color?: string;
  title_color?: string;
  title_highlight_color?: string;
  description_color?: string;
  card_title_color?: string;
  card_description_color?: string;
  divider_color?: string;
  metadata?: Banner8MetaItem[];
}

interface Banner8Props {
  data: Banner8Data | { data: Banner8Data };
}

function resolvePayload(
  data: Banner8Props["data"],
): Banner8Data | undefined {
  const anyData = data as { data?: Banner8Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as Banner8Data;
  }
  return data as Banner8Data;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Wraps `{highlight}` / `{{line}}` segments in a span styled with the supplied
 * highlight color. Mirrors `formatSectionTitle` semantics but applies the color
 * via inline `style=` so admin-driven hex values don't need Tailwind JIT.
 */
function formatTitleWithHighlight(title: string, highlightColor: string): string {
  if (!title) return "";
  const safeColor = highlightColor.replace(/[^#a-zA-Z0-9(),.%\s]/g, "");
  const openTag = `<span style="color:${safeColor}">`;

  if (title.includes("{{")) {
    return title
      .replace(/\{\{([^}]+)\}\}/g, (_m, inner) => `${openTag}${escapeHtml(inner)}</span><br/>`)
      .replace(/\s+/g, " ");
  }

  if (title.includes("{")) {
    return title
      .replace(/\{/g, openTag)
      .replace(/\}/g, "</span>")
      .replace(/\s+/g, " ");
  }

  return escapeHtml(title);
}

const Banner8 = ({ data }: Banner8Props) => {
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);
  const isMobile = useIsMobile(768);

  const {
    status = true,
    title = "",
    description = "",
    image = "",
    mobile_image = "",
    image_alt = "",
    background_color = "#000000",
    title_color = "#FFFFFF",
    title_highlight_color = "#FFD700",
    description_color = "#FFFFFF",
    card_title_color = "#FFFFFF",
    card_description_color = "#FFFFFF",
    divider_color = "#F8FAFC",
    metadata = [],
  } = sectionData || {};

  const sortedCards = useMemo(() => {
    if (!Array.isArray(metadata) || metadata.length === 0) return [];
    return [...metadata]
      .filter((m) => m && (m.status === undefined || m.status === true))
      .sort(
        (a, b) =>
          Number(a.priority ?? 0) - Number(b.priority ?? 0),
      );
  }, [metadata]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current && status) {
  //     FireWidgetViewedEvent(ref, "banner_8");
  //   }
  // }, [status]);

  if (!status) return null;

  // Use mobile-specific image when on mobile (after hydration); otherwise desktop.
  const bgImage = isMobile && mobile_image ? mobile_image : image;

  return (
    <section
      ref={ref}
      data-banner="8"
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "relative w-full overflow-hidden",
        "flex flex-col items-stretch",
        "py-[52px] px-[26px] gap-10",
        "md:pt-9 md:py-16 md:px-10 md:gap-20",
        "lg:px-14",
      )}
      style={{
        backgroundColor: background_color,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {bgImage ? (
        <Image
          src={bgImage}
          alt={image_alt || ""}
          fill
          className="-z-0 object-cover object-center"
          sizes="100vw"
          priority={false}
        />
      ) : null}

      <div className="relative z-10 mx-auto flex flex-col items-center gap-[30px] md:gap-4">
        {title ? (
          <h2
            className="text-center text-[28px] font-bold leading-tight md:text-[40px]"
            style={{ color: title_color }}
            dangerouslySetInnerHTML={{
              __html: formatTitleWithHighlight(title, title_highlight_color),
            }}
          />
        ) : null}
        {description ? (
          <p
            className="w-[308px] whitespace-pre-wrap text-center text-[14px] md:text-[16px] lg:w-[686px]"
            style={{ color: description_color }}
          >
            {description}
          </p>
        ) : null}
      </div>

      {sortedCards.length > 0 ? (
        <div className="relative z-10 flex w-full">
          <div
            className={cn(
              "mx-auto flex w-full flex-col items-center justify-center flex-wrap gap-6",
              "md:w-[866px] md:flex-row md:items-start md:gap-x-0 md:gap-y-20",
            )}
          >
            {sortedCards.map((item, index) => (
              <React.Fragment key={item.id ?? `card-${index}`}>
                <InfoCard
                  item={item}
                  titleColor={card_title_color}
                  descriptionColor={card_description_color}
                />
                {/* Desktop/tablet divider — after every other card except the last. */}
                {index % 2 === 0 && index !== sortedCards.length - 1 ? (
                  <hr
                    className="mx-[60px] my-0 hidden h-[112px] w-0.5 border-0 md:block lg:h-[112px]"
                    style={{ backgroundColor: divider_color }}
                  />
                ) : null}
                {/* Mobile divider — after every card except the last. */}
                {index !== sortedCards.length - 1 ? (
                  <hr
                    className="mx-2 my-2 block h-[1px] w-full border-0 md:hidden"
                    style={{ backgroundColor: divider_color }}
                  />
                ) : null}
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
};

interface InfoCardProps {
  item: Banner8MetaItem;
  titleColor: string;
  descriptionColor: string;
}

/**
 * Inlined card that mirrors the IITKGP InfoCard layout used inside KeySkills:
 * transparent background (so the section background shows through), icon,
 * title, and description stacked vertically (centered on mobile, left-aligned
 * on desktop).
 */
const InfoCard = ({ item, titleColor, descriptionColor }: InfoCardProps) => {
  const {
    metaImg,
    altImg,
    metaTitle = "",
    metaDescription = "",
  } = item;

  return (
    <div
      className={cn(
        "flex flex-col bg-transparent items-center",
        "md:flex-row md:!items-center md:w-[280px]",
        "lg:flex-row lg:!items-center lg:w-[366px]",
        "md:gap-4",
      )}
    >
      {metaImg ? (
        <div className="relative h-14 w-14 shrink-0 rounded-lg md:size-[52px]">
          <Image
            src={metaImg}
            alt={altImg || metaTitle || ""}
            fill
            className="rounded-lg object-cover"
            sizes="52px"
          />
        </div>
      ) : null}
      <div
        className={cn(
          "flex w-[266px] flex-col items-center md:items-start",
          "md:gap-1.5",
        )}
      >
        {metaTitle ? (
          <h3
            className={cn(
              "w-[266px] text-center font-semibold leading-normal md:w-[296px] md:text-left md:text-xl",
            )}
            style={{ color: titleColor }}
          >
            {metaTitle}
          </h3>
        ) : null}
        {metaDescription ? (
          <p
            className={cn(
              "w-[266px] text-center text-[12px] md:w-[254px] md:text-left md:text-[14px]",
            )}
            style={{ color: descriptionColor }}
          >
            {metaDescription}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default Banner8;
